// views/SubmitToolForm.js
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../constants/firebase';
import { collection, addDoc } from 'firebase/firestore';
import { sections } from '../constants/menuItems';

function SubmitTool() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    label: '',
    link: '',
    image: '',
    info: '',
    warning: '',
    section: '',
    category: 'companyOwned'
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  // Alle Sections für das Dropdown sammeln
  const allSections = [];
  Object.entries(sections).forEach(([categoryName, categoryItems]) => {
    Object.entries(categoryItems).forEach(([key, section]) => {
      allSections.push({
        id: key,
        label: section.label,
        categoryName
      });
    });
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const toolsRef = collection(db, 'tools');
      
      await addDoc(toolsRef, {
        ...formData,
        status: 'pending', // Neues Tool ist erstmal pending
        createdBy: auth.currentUser.email,
        createdAt: new Date(),
        sectionCategory: allSections.find(s => s.id === formData.section)?.categoryName || 'Generieren'
      });

      setSuccess(true);
      
      // Nach 2 Sekunden zur Startseite zurück
      setTimeout(() => {
        navigate('/');
      }, 2000);
      
    } catch (err) {
      console.error('Fehler beim Hinzufügen:', err);
      setError('Tool konnte nicht hinzugefügt werden: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (success) {
    return (
      <main className="px-8 py-12 max-w-2xl mx-auto">
        <div className="bg-green-500/10 border border-green-500 text-green-500 px-6 py-4 rounded-lg text-center">
          <h2 className="text-xl font-bold mb-2">✅ Tool eingereicht!</h2>
          <p>Dein Tool wurde erfolgreich eingereicht und wartet auf Freigabe.</p>
        </div>
      </main>
    );
  }

  return (
    <main className="px-8 py-12 max-w-2xl mx-auto">
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate('/')}
          className="text-gray-400 hover:text-white"
        >
          ← Zurück
        </button>
      </div>

      <h1 className="text-3xl font-bold text-white mb-2">Neues Tool einreichen</h1>
      <p className="text-gray-400 mb-8">
        Füge ein neues KI-Tool hinzu.
      </p>

      {error && (
        <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Tool-Name */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Tool-Name *
          </label>
          <input
            type="text"
            name="label"
            value={formData.label}
            onChange={handleChange}
            required
            placeholder="z.B. Adobe Firefly"
            className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
          />
        </div>

        {/* URL */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Tool-URL *
          </label>
          <input
            type="url"
            name="link"
            value={formData.link}
            onChange={handleChange}
            required
            placeholder="https://..."
            className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
          />
        </div>

        {/* Kategorie (Section) */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Kategorie *
          </label>
          <select
            name="section"
            value={formData.section}
            onChange={handleChange}
            required
            className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
          >
            <option value="">Bitte wählen...</option>
            {allSections.map(section => (
              <option key={section.id} value={section.id}>
                {section.categoryName} → {section.label}
              </option>
            ))}
          </select>
        </div>

        {/* Tool-Typ */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Tool-Typ *
          </label>
          <select
            name="category"
            value={formData.category}
            onChange={handleChange}
            required
            className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
          >
            <option value="companyOwned">Lizenzierte Tools</option>
            <option value="free">Kostenfreie Tools</option>
          </select>
        </div>

        {/* Info */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Info-Text
          </label>
          <input
            type="text"
            name="info"
            value={formData.info}
            onChange={handleChange}
            placeholder="z.B. Zugangsdaten bei Tim anfragen"
            className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
          />
        </div>

        {/* Warnung */}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Warnung
          </label>
          <input
            type="text"
            name="warning"
            value={formData.warning}
            onChange={handleChange}
            placeholder="z.B. Nicht für sensible Inhalte geeignet"
            className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
          />
        </div>

        {/* Submit Button */}
        <div className="flex gap-4">
          <button
            type="submit"
            disabled={loading}
            className="flex-1 bg-blue-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Wird eingereicht...' : 'Tool einreichen'}
          </button>
          
          <button
            type="button"
            onClick={() => navigate('/')}
            className="px-6 py-3 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors"
          >
            Abbrechen
          </button>
        </div>
      </form>
    </main>
  );
}

export default SubmitTool;